import SockJS from 'sockjs-client';
import { Client } from '@stomp/stompjs';

import authService from './authService';
import { API_URL } from '@/config/api';

function createClientEventId(prefix) {
    return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function resolveSockJsUrl() {
    const apiUrl = new URL(API_URL, window.location.origin);

    return `${apiUrl.protocol}//${apiUrl.host}/ws-bible`;
}

class BibleStompClient {
    constructor() {
        this.client = null;
        this.connected = false;
        this.connectPromise = null;
        this.statusHandler = null;
        this.subscriptions = {};
    }

    setStatusHandler(handler) {
        this.statusHandler = handler;
    }

    notifyStatus(status) {
        if (this.statusHandler) {
            this.statusHandler(status);
        }
    }

    connect() {
        if (this.connected && this.client?.connected) {
            return Promise.resolve(this);
        }

        if (this.connectPromise) {
            return this.connectPromise;
        }

        const token = authService.token.get();

        if (!token) {
            const error = new Error('Token de autenticacao nao encontrado para conectar no estudo biblico.');
            this.notifyStatus('ERROR');
            return Promise.reject(error);
        }

        this.notifyStatus('CONNECTING');

        this.connectPromise = new Promise((resolve, reject) => {
            let settled = false;

            const resolveOnce = (value) => {
                if (settled) return;
                settled = true;
                resolve(value);
            };

            const rejectOnce = (error) => {
                if (settled) return;
                settled = true;
                this.connectPromise = null;
                reject(error);
            };

            this.client = new Client({
                webSocketFactory: () => new SockJS(resolveSockJsUrl()),

                connectHeaders: {
                    Authorization: `Bearer ${token}`,
                },

                reconnectDelay: 5000,
                heartbeatIncoming: 10000,
                heartbeatOutgoing: 10000,

                debug: (message) => {
                    if (process.env.NODE_ENV !== 'production') {
                        console.log('[Bible STOMP]', message);
                    }
                },

                onConnect: () => {
                    this.connected = true;
                    this.notifyStatus('CONNECTED');
                    resolveOnce(this);
                },

                onStompError: (frame) => {
                    this.connected = false;
                    this.notifyStatus('ERROR');

                    const message =
                        frame?.body ||
                        frame?.headers?.message ||
                        'Backend recusou a conexao STOMP do estudo biblico.';

                    rejectOnce(new Error(message));
                },

                onWebSocketError: (event) => {
                    this.connected = false;
                    this.notifyStatus('ERROR');

                    rejectOnce(event instanceof Error
                        ? event
                        : new Error('Falha ao abrir WebSocket do estudo biblico.')
                    );
                },

                onWebSocketClose: () => {
                    this.connected = false;
                    this.connectPromise = null;
                    this.notifyStatus('DISCONNECTED');
                },

                onDisconnect: () => {
                    this.connected = false;
                    this.connectPromise = null;
                    this.notifyStatus('DISCONNECTED');
                },
            });

            this.client.activate();
        });

        return this.connectPromise;
    }

    subscribe(destination, callback) {
        if (!this.connected || !this.client?.connected) {
            throw new Error('WebSocket ainda nao conectado');
        }

        const subscription = this.client.subscribe(destination, (message) => {
            try {
                callback(message.body ? JSON.parse(message.body) : null);
            } catch (error) {
                console.error('Erro ao processar mensagem STOMP:', error);
            }
        });

        this.subscriptions[destination] = subscription;

        return () => {
            try {
                subscription.unsubscribe();
            } finally {
                delete this.subscriptions[destination];
            }
        };
    }

    send(destination, payload) {
        if (!this.connected || !this.client?.connected) {
            return false;
        }

        this.client.publish({
            destination,
            headers: {
                'content-type': 'application/json',
            },
            body: JSON.stringify(payload || {}),
        });

        return true;
    }

    disconnect() {
        Object.values(this.subscriptions).forEach((subscription) => {
            try {
                subscription.unsubscribe();
            } catch (error) {
                console.warn('Erro ao remover subscription STOMP:', error);
            }
        });

        this.subscriptions = {};
        this.connected = false;
        this.connectPromise = null;

        if (this.client?.active) {
            this.client.deactivate();
        }

        this.client = null;
        this.notifyStatus('DISCONNECTED');
    }
}

export { createClientEventId };
export default BibleStompClient;